import { AutoThemeIcon, MoonIcon, SunIcon } from './icons';
import type { ThemeMode } from '../theme/theme';

/** Props for {@link ThemeToggle}. */
export interface ThemeToggleProps {
  /** The current theme mode. */
  mode: ThemeMode;
  /** Advance to the next mode in the cycle (light → dark → auto → …). */
  onCycle: () => void;
  /** Optional extra classes. */
  className?: string;
}

/** Icon + human label for each mode, used for rendering and the aria-label. */
const MODE_META: Record<
  ThemeMode,
  { label: string; Icon: (props: { size?: number }) => JSX.Element }
> = {
  light: { label: 'Light', Icon: SunIcon },
  dark: { label: 'Dark', Icon: MoonIcon },
  auto: { label: 'Auto', Icon: AutoThemeIcon },
};

/** Next mode in the cycle — kept in sync with THEME_CYCLE for the aria hint. */
const NEXT_LABEL: Record<ThemeMode, string> = {
  light: 'Dark',
  dark: 'Auto',
  auto: 'Light',
};

/**
 * Accessible theme switcher.
 *
 * Behaviour: a single button that cycles light → dark → auto. A single control
 * (rather than three radio buttons) keeps the header chrome compact while staying
 * fully keyboard-operable.
 *
 * Accessibility:
 * - Native `<button>` — focusable and activatable with Enter/Space.
 * - `aria-label` announces the current mode AND what the next activation does, so
 *   screen-reader users understand the cycle without seeing all states.
 * - The icon is decorative; the accessible name carries the meaning.
 * - Visible `focus-visible` ring; adequate hit target (min 40px).
 */
export function ThemeToggle({ mode, onCycle, className = '' }: ThemeToggleProps): JSX.Element {
  const { label, Icon } = MODE_META[mode];
  return (
    <button
      type="button"
      onClick={onCycle}
      aria-label={`Theme: ${label}. Activate to switch to ${NEXT_LABEL[mode]}.`}
      title={`Theme: ${label}`}
      className={
        'inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium ' +
        'text-slate-700 ring-1 ring-inset ring-slate-300 transition-colors ' +
        'hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 ' +
        'focus-visible:outline-offset-2 focus-visible:outline-brand-500 ' +
        'dark:text-slate-200 dark:ring-navy-border dark:hover:bg-navy-surface ' +
        className
      }
    >
      <Icon size={18} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
